import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  ModalProps,
  notification,
  Row,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from 'antd';
import {
  useDefaultEmptyFieldError,
  useDisclosure,
  useRouteMatchParam,
} from '@/hooks';
import { Customer } from '@/core/domain/people/Customers/Customers.types';
import useWorkOrderAddPolicy, {
  useUpdateWorkOrder,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import { FaEdit, FaUserEdit, FaUserPlus } from 'react-icons/fa';
import CustomerSimplifiedRegisterModal from '@/components/Customers/CustomerSimplifiedRegisterModal';
import { CustomerVehicleReduced } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import CustomerVehicleSimplifiedRegisterModal from '@/components/Customers/CustomerVehicleSimplifiedRegisterModal';
import { FaCar } from 'react-icons/fa';
import {
  WorkOrderComplementStatus,
  WorkOrderComplementTankLevel,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useWorkorderDetailContext } from '../../Details/WorkorderDetailProvider';
import CustomersSelectField from '@/components/_common/CustomersSelectField';
import VehicleSelectField from '@/components/_common/VehicleSelectField';
import { queryClient } from '@/Root';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { usePortoPoliciesList } from '@/core/domain/porto/policies/porto-policies.hooks';
import moment, { Moment } from 'moment';
import { isAppPortocar } from '@/index';
import { PortoPolicy } from '@/core/domain/porto/policies/porto-policies.types';
import { useWorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import {
  WorkOrderAddPolicyCompanyCode,
  WorkOrderAddPolicyRequest,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import PortoPolicyCard from '@/components/_common/PortoPolicyCard';

const { Title } = Typography;

interface FormData {
  companyId: number;
  customerId: number;
  vehicleId?: number;
  lastOilChange?: Moment;
  lastOilChangeKilometers?: number;
  currentKilometers?: number;
  prisma?: string;
  tankLevel?: WorkOrderComplementTankLevel;
  policy?: {
    companyCode?: string;
    policyNumber?: string;
    customer?: {
      birthDate?: string;
      documentNumber?: string;
      contacts?: { email: string; phone: string };
      name?: string;
    };
    vehicle?: {
      chassi?: string;
      model?: string;
      brand?: string;
      yearFabrication?: number;
      yearModel?: number;
    };
    benefits: PortoPolicy['benefits'];
  };
}

interface Props extends ModalProps {
  onCancel: VoidFunction;
}

function UpdateWorkOrderModal({ open, onCancel }: Props) {
  const intl = useIntl();
  const queryChanel = useQueryChanel();
  const requiredField = useDefaultEmptyFieldError();

  const [form] = Form.useForm<FormData>();

  const customerId = Form.useWatch('customerId', form);
  const vehicleId = Form.useWatch('vehicleId', form);
  const workOrderId = useRouteMatchParam('workOrderId');
  const policyNumber = Form.useWatch(['policy', 'policyNumber'], form);

  const [licensePlate, setLicensePlate] = useState<string>();
  const [vehicleSelected, setVehicleSelected] =
    useState<CustomerVehicleReduced>();

  const hasFuelEletric = useMemo(() => {
    return vehicleSelected?.fuel?.name?.toLowerCase()?.includes('elétrico');
  }, [vehicleSelected]);

  const { data: configBudget, mutateAsync: configBudgetMutate } =
    useWorkorderConfigurationCompany();

  const {
    workorder: { data: workOrder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const updateMutation = useUpdateWorkOrder(+workOrderId);
  const addPolicy = useWorkOrderAddPolicy();

  const [modalCustomerEditVisible, modalCustomerEditAction] = useDisclosure();
  const [modalCustomerCreateVisible, modalCustomerCreateAction] =
    useDisclosure();
  const [modalVehicleEditVisible, modalVehicleEditAction] = useDisclosure();
  const [modalVehicleCreateVisible, modalVehicleCreateAction] = useDisclosure();

  const policiesList = usePortoPoliciesList(
    licensePlate,
    open && isAppPortocar && licensePlate !== workOrder?.vehicle?.licensePlate,
  );

  const hasPolicies = useMemo(
    () => (policiesList.data?.length || 0) > 0,
    [policiesList.data, licensePlate],
  );

  const hasPolicyInComplement = useMemo(
    () => !!complement?.policyNumber,
    [complement?.policyNumber],
  );

  useEffect(() => {
    if (open && workOrder) {
      form.setFieldsValue({
        companyId: workOrder?.company.id,
        customerId: workOrder?.customer?.id,
        vehicleId: workOrder?.vehicle?.id,
      });

      if (workOrder?.company.id && isAppPortocar) {
        (async () => await configBudgetMutate(workOrder?.company.id))();
      }
    }
  }, [workOrder, open, isAppPortocar]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();

      if (isAppPortocar && hasPolicies && values.policy?.policyNumber) {
        await addPolicy.mutateAsync({
          companyCode:
            WorkOrderAddPolicyCompanyCode[String(values.policy?.companyCode)],
          complementId: Number(complement?.id),
          lastOilChange: String(values.lastOilChange?.format()),
          lastOilChangeKilometers: Number(values.lastOilChangeKilometers),
          policyNumber: String(values.policy.policyNumber),
          vehicleId: Number(values.vehicleId),
          vehicle: {
            ...values.policy.vehicle,
            currentKilometers: Number(values.currentKilometers),
            licensePlate: String(licensePlate),
            prisma: values.prisma,
            tankLevel: values.tankLevel,
          } as WorkOrderAddPolicyRequest['vehicle'],
        });
      } else {
        await updateMutation.mutateAsync({
          companyId: values.companyId,
          vehicleId: values.vehicleId,
          customerId: !values.vehicleId ? values.customerId : undefined,
        });

        notification.success({
          message: 'Sucesso',
          description: 'Orçamento atualizado.',
        });
      }

      queryChanel({
        resetQueries: [
          ['workOrders/tracking/calendar/complement', complement?.id],
        ],
      });

      form.resetFields();

      handleCancel();
    } catch {}
  }, [hasPolicies, complement, licensePlate, isAppPortocar]);

  const handleCancel = useCallback(() => {
    if (policiesList.isLoading) return;

    onCancel();

    if (isAppPortocar) {
      policiesList.remove();
      setLicensePlate(undefined);
    }
  }, [policiesList.isLoading]);

  const handleCustomerEditFinishing = async () => {
    modalCustomerEditAction.close();
    await queryClient.invalidateQueries(['workOrders']);

    queryClient.removeQueries(['workOrders']);
    queryClient.removeQueries(['workOrders/list']);
  };

  const handleCustomerCreateFinishing = async (custormerCreated: Customer) => {
    form.setFieldValue('customerId', custormerCreated.id);
    await form.validateFields();
    modalCustomerCreateAction.close();
  };

  const handleVehicleCreateFinishing = async (
    customerVehicle: CustomerVehicleReduced,
  ) => {
    if (customerVehicle) {
      form.setFieldValue('vehicleId', customerVehicle.id);
      modalVehicleCreateAction.close();
    }
  };

  const disableCompany = complement?.status !== WorkOrderComplementStatus.Open;

  return (
    <>
      <Modal
        title={intl.formatMessage({ id: 'workOrders.details.update' })}
        open={open}
        onOk={handleSubmit}
        okText="Salvar"
        onCancel={handleCancel}
        confirmLoading={updateMutation.isLoading || addPolicy.isLoading}
        destroyOnClose
        okButtonProps={{
          disabled:
            policiesList.isLoading || (!vehicleId && hasPolicyInComplement),
        }}
        cancelButtonProps={{
          disabled: policiesList.isLoading || addPolicy.isLoading,
        }}
        closable={!policiesList.isLoading || !addPolicy.isLoading}
        maskClosable={!policiesList.isLoading || !addPolicy.isLoading}
      >
        <Form form={form}>
          {isAppPortocar && (
            <>
              <Form.Item
                hidden
                name={['policy', 'companyCode']}
                rules={
                  !!policiesList.data?.length
                    ? [{ required: true, message: 'Selecione uma apólice' }]
                    : undefined
                }
              />
              <Form.Item hidden name={['policy', 'policyNumber']} />
              <Form.Item hidden name={['policy', 'customer']} />
              <Form.Item hidden name={['policy', 'vehicle']} />
              <Form.Item hidden name={['policy', 'benefits']} />
            </>
          )}

          <FloatingFormItem
            name="companyId"
            rules={[requiredField]}
            label={intl.formatMessage({
              id: 'workOrders.domain.company',
            })}
            getValueFromEvent={(value) => {
              if (value && isAppPortocar) {
                configBudgetMutate(value);
              }
              return value;
            }}
          >
            <CompaniesInfiniteSelectField disabled={disableCompany} />
          </FloatingFormItem>

          <Row gutter={[16, 8]} wrap={false}>
            <Col flex={1}>
              <FloatingFormItem
                name="customerId"
                label={intl.formatMessage({
                  id: 'workOrders.domain.customer',
                })}
                rules={[requiredField]}
                getValueFromEvent={(id) => {
                  form.resetFields(['vehicleId']);
                  return id;
                }}
              >
                <CustomersSelectField
                  key={String(modalCustomerEditVisible)}
                  showCpfCnpj
                />
              </FloatingFormItem>
            </Col>

            <Col>
              {!!customerId && (
                <Tooltip
                  title={intl.formatMessage({
                    id: `workOrders.domain.complements.customerEdit`,
                  })}
                >
                  <Button
                    size="large"
                    icon={<FaUserEdit />}
                    onClick={modalCustomerEditAction.open}
                  />
                </Tooltip>
              )}
              {!customerId && (
                <Tooltip
                  title={intl.formatMessage({
                    id: `workOrders.domain.complements.customerCreate`,
                  })}
                >
                  <Button
                    size="large"
                    icon={<FaUserPlus />}
                    onClick={modalCustomerCreateAction.open}
                  />
                </Tooltip>
              )}
            </Col>
          </Row>

          <Row gutter={[16, 8]} wrap={false}>
            <Col flex={1}>
              <FloatingFormItem
                name="vehicleId"
                label={intl.formatMessage({
                  id: 'workOrders.domain.vehicle',
                })}
                getValueFromEvent={(value) => {
                  if (isAppPortocar) {
                    form.setFields([
                      { name: ['policy', 'companyCode'], errors: [] },
                    ]);

                    if (!value) {
                      setLicensePlate(undefined);
                      policiesList.remove();
                    }
                  }

                  return value;
                }}
              >
                <VehicleSelectField
                  disabled={policiesList.isLoading}
                  key={String(modalVehicleEditVisible)}
                  customerId={customerId}
                  allowClear
                  onVehicleSelect={(vehicle) => {
                    if (vehicle) {
                      if (isAppPortocar) {
                        setLicensePlate(vehicle.licensePlate);
                        setVehicleSelected(vehicle);
                      }

                      if (!customerId) {
                        form.setFields([
                          {
                            name: 'customerId',
                            value: vehicle.customerId,
                            errors: [],
                          },
                          { name: 'vehicleId', value: vehicle.id },
                        ]);
                      }
                    }
                  }}
                  queryEnable={!!customerId && !vehicleId}
                />
              </FloatingFormItem>
            </Col>

            <Col>
              <Tooltip
                title={intl.formatMessage({
                  id: `workOrders.domain.complements.${
                    !!vehicleId ? 'vehicleEdit' : 'vehicleCreate'
                  }`,
                })}
              >
                <Button
                  size="large"
                  disabled={!customerId}
                  onClick={
                    !!vehicleId
                      ? modalVehicleEditAction.open
                      : modalVehicleCreateAction.open
                  }
                  icon={!!vehicleId ? <FaEdit /> : <FaCar />}
                />
              </Tooltip>
            </Col>
          </Row>

          {hasPolicies && isAppPortocar && (
            <>
              <Row gutter={[16, 8]}>
                <Col span={24}>
                  <Title level={4}>
                    <FormattedMessage id="workOrders.domain.vehicle" />
                  </Title>
                </Col>

                <Col span={8}>
                  <FloatingFormItem
                    name="prisma"
                    label={intl.formatMessage({
                      id: 'workOrders.domain.complements.prisma',
                    })}
                    rules={[
                      {
                        required: configBudget?.prisma,
                        message: intl.formatMessage({
                          id: 'workOrders.fieldRequiredByConfigCompany',
                        }),
                      },
                    ]}
                  >
                    <Input />
                  </FloatingFormItem>
                </Col>

                <Col span={8}>
                  <FloatingFormItem
                    name="currentKilometers"
                    label={intl.formatMessage({
                      id: 'workOrders.domain.complements.currentKilometers',
                    })}
                    rules={[
                      {
                        required: isAppPortocar || configBudget?.isKmRequired,
                        message: intl.formatMessage({
                          id: 'workOrders.fieldRequiredByConfigCompany',
                        }),
                      },
                    ]}
                  >
                    <InputDecimal fractions={0} min={0} max={5000000} />
                  </FloatingFormItem>
                </Col>

                <Col span={8}>
                  <FloatingFormItem
                    name="tankLevel"
                    label={intl.formatMessage({
                      id: 'workOrders.domain.complements.tankLevel',
                    })}
                    rules={[
                      {
                        required: configBudget?.tankLevel,
                        message: intl.formatMessage({
                          id: 'workOrders.fieldRequiredByConfigCompany',
                        }),
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      style={{ width: '100%' }}
                      options={Object.values(WorkOrderComplementTankLevel).map(
                        (a) => ({
                          label: intl.formatMessage({
                            id: `workOrders.${a}`,
                          }),
                          value: a,
                        }),
                      )}
                    />
                  </FloatingFormItem>
                </Col>

                <Col span={12}>
                  <FloatingFormItem
                    name="lastOilChange"
                    label={intl.formatMessage({
                      id: 'workOrders.domain.complements.lastOilChange',
                    })}
                    rules={hasFuelEletric ? [] : [requiredField]}
                  >
                    <InputDate
                      disabledDate={(date) => date.isAfter(moment(), 'day')}
                    />
                  </FloatingFormItem>
                </Col>

                <Col span={12}>
                  <FloatingFormItem
                    name="lastOilChangeKilometers"
                    label={intl.formatMessage({
                      id: 'workOrders.domain.complements.lastOilChangeKilometers',
                    })}
                    rules={hasFuelEletric ? [] : [requiredField]}
                  >
                    <InputDecimal fractions={0} min={0} />
                  </FloatingFormItem>
                </Col>
              </Row>
            </>
          )}

          {isAppPortocar && (
            <Row gutter={[16, 8]}>
              {policiesList.isLoading && (
                <Col span={24}>
                  <Skeleton loading />
                </Col>
              )}

              {hasPolicies && (
                <>
                  <Col span={24}>
                    <Title level={4}>Apólices do veículo</Title>

                    <Form.Item shouldUpdate noStyle>
                      {({ getFieldError }) => {
                        const errors = getFieldError(['policy', 'companyCode']);
                        return errors.length ? (
                          <span style={{ color: 'red' }}>{errors[0]}</span>
                        ) : null;
                      }}
                    </Form.Item>

                    {policiesList.data?.map((policy) => (
                      <PortoPolicyCard
                        style={{ marginTop: 16 }}
                        key={policy.policyNumber}
                        active={policy.policyNumber === policyNumber}
                        onClick={() => {
                          form.setFields([{ name: 'customerId', errors: [] }]);

                          if (policyNumber === policy.policyNumber) {
                            form.setFieldValue('policy', undefined);
                          } else {
                            const { email, phone, ...restCustomer } =
                              policy.customer;

                            form.setFieldsValue({
                              policy: {
                                companyCode: policy.companyCode,
                                policyNumber: policy.policyNumber,
                                customer: {
                                  ...restCustomer,
                                  contacts: { email, phone },
                                },
                                vehicle: policy.vehicle,
                                benefits: policy.benefits,
                              },
                            });
                          }
                        }}
                        policy={policy}
                        vehicle={{
                          ...policy.vehicle,
                          licensePlate: licensePlate as string,
                        }}
                      />
                    ))}
                  </Col>
                </>
              )}
            </Row>
          )}
        </Form>
      </Modal>

      <CustomerSimplifiedRegisterModal
        visible={modalCustomerEditVisible}
        handleVisible={modalCustomerEditAction.toggle}
        customerId={customerId}
        onFinishCallback={handleCustomerEditFinishing}
      />

      <CustomerSimplifiedRegisterModal
        visible={modalCustomerCreateVisible}
        handleVisible={modalCustomerCreateAction.toggle}
        onFinishCallback={handleCustomerCreateFinishing}
      />

      <CustomerVehicleSimplifiedRegisterModal
        visible={modalVehicleEditVisible}
        handleVisible={modalVehicleEditAction.toggle}
        customerId={customerId}
        vehicleId={vehicleId}
        key={vehicleId}
        onFinish={modalVehicleEditAction.close}
      />

      <CustomerVehicleSimplifiedRegisterModal
        visible={modalVehicleCreateVisible}
        handleVisible={modalVehicleCreateAction.close}
        customerId={customerId}
        onFinish={handleVehicleCreateFinishing}
      />
    </>
  );
}

export default UpdateWorkOrderModal;
